/**
 * CollisionTypes - enums and data structures for physics collision interactions.
 */

/** Collision channels for gameplay and physics filtering. CustomGameChannel1-8 can be named via CustomChannelConfig. */
export enum CollisionChannel {
  WorldStatic = 'worldStatic',
  WorldDynamic = 'worldDynamic',
  Pawn = 'pawn',
  Visibility = 'visibility',
  Camera = 'camera',
  PhysicsBody = 'physicsBody',
  Vehicle = 'vehicle',
  Destructible = 'destructible',

  CustomGameChannel1 = 'customGameChannel1',
  CustomGameChannel2 = 'customGameChannel2',
  CustomGameChannel3 = 'customGameChannel3',
  CustomGameChannel4 = 'customGameChannel4',
  CustomGameChannel5 = 'customGameChannel5',
  CustomGameChannel6 = 'customGameChannel6',
  CustomGameChannel7 = 'customGameChannel7',
  CustomGameChannel8 = 'customGameChannel8',

  // for now we can only suport a maximum of 16 channels
}

/** Collision setting for a collision profile */
export enum CollisionMode {
  NoCollision = 'noCollision',
  QueryOnly = 'queryOnly',
  PhysicsOnly = 'physicsOnly',
  QueryAndPhysics = 'queryAndPhysics',
}

/** Collision response for a collision profile or channel */
export enum CollisionResponse {
  Ignore = 'ignore',
  Overlap = 'overlap',
  Block = 'block',
}

/** Default collision profiles */
export enum DefaultCollisionProfile {
  NoCollision = 'NoCollision',
  BlockAll = 'BlockAll',
  OverlapAll = 'OverlapAll',
  BlockAllDynamic = 'BlockAllDynamic',
  OverlapAllDynamic = 'OverlapAllDynamic',
  IgnoreOnlyPawns = 'IgnoreOnlyPawns',
  Pawn = 'Pawn',
  Character = 'Character',
  Destructible = 'Destructible',
  InvisibleWall = 'InvisibleWall',
  InvisibleWallDynamic = 'InvisibleWallDynamic',
  Trigger = 'Trigger',
  Ragdoll = 'Ragdoll',
  Vehicle = 'Vehicle',
  UI = 'UI',
  SpringArmCamera = 'SpringArmCamera',
  Default = BlockAll,
}

/** Channel response for a collision profile */
export class ChannelResponse {
  /** The name of the channel. Should match an existing channel in the collision config.
   * It's a string instead of an enum so that users can apply names to the predefined game trace channels.
   * The user name is configured in the defaultChannelResponse array
   */
  channel: string = '';
  /** The collision response */
  response: CollisionResponse = CollisionResponse.Block;
}

/** Names and configures custom game collision channels */
export class CustomChannelConfig {
  channel: CollisionChannel;
  defaultResponse: CollisionResponse;
  name: string;

  constructor(channel: CollisionChannel, defaultResponse: CollisionResponse, name: string) {
    this.channel = channel;
    this.defaultResponse = defaultResponse;
    this.name = name;
  }
}

/** Defines collision detection mode and channel responses for a component */
export class CollisionProfile {
  /** The name of the collision response template. Should be unique */
  name: string;
  /** The mode of collision detection to use */
  mode: CollisionMode;
  /** The channel of the object to apply the collision response to */
  objectChannel: CollisionChannel;
  /** The collision responses to apply */
  responses: Array<ChannelResponse>;

  constructor(
    name: string,
    mode: CollisionMode,
    objectChannel: CollisionChannel,
    responses: Array<ChannelResponse> = []
  ) {
    // Create default responses for all channels
    this.responses = Object.values(CollisionChannel).map(value => ({
      channel: value,
      response: CollisionResponse.Block
    }));

    // Override with provided responses
    responses.forEach(response => {
      const index = this.responses.findIndex(r => r.channel === response.channel);
      if (index !== -1) {
        this.responses[index] = response;
      }
    });

    this.name = name;
    this.mode = mode;
    this.objectChannel = objectChannel;
  }
}

/** Customization of a collision response profile */
export class CollisionResponseCustomization {
  /** The name of the collision response profile to customize */
  profileName: string = '';
  /** The channel response customizations to apply */
  responseCustomizations: Array<ChannelResponse> = [];

  constructor(profileName: string, responseCustomizations: Array<ChannelResponse>) {
    this.profileName = profileName;
    this.responseCustomizations = responseCustomizations;
  }
}

export interface CollisionProfileData {
  name: string;
  mode: CollisionMode;
  objectChannelName: string;
  responses?: Array<ChannelResponse>;
}

export interface CustomChannelConfigData {
  channel: CollisionChannel;
  defaultResponse: CollisionResponse;
  name: string;
}

export interface CollisionResponseCustomizationData {
  profileName: string;
  responseCustomizations: Array<ChannelResponse>;
}

export interface CollisionConfigData {
  userProfiles?: Array<CollisionProfileData>;
  customChannelConfigs?: Array<CustomChannelConfigData>;
  profileOverrides?: Array<CollisionResponseCustomizationData>;
}
